import { useMemo, useState } from "react";
import { Icon } from "@/components/atoms/Icon";
import { Field } from "@/components/organisms/Field";
import { usePageThreadReference } from "@/components/organisms/Thread/Thread.hooks";
import { Tree } from "@/components/organisms/Tree";
import { useScope } from "@/hooks/useScope";
import { State } from "@/state";
import { Button } from "@/components/ui/button";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { TagsIcon } from "lucide-react";

const CHART_OF_ACCOUNTS_NODE_QUERY = gql`
  query ChartOfAccountsNode(
    $id: ID!
    $startDate: DateTime
    $endDate: DateTime
    $entityIdentifiers: [String!]
  ) {
    node(id: $id) {
      ... on ChartedAccountObject {
        id
        identifier
        createdAt
        name
        type
        subtype
        entity {
          id
          identifier
          name
        }
        childrenAcrossEntities(entityIdentifiers: $entityIdentifiers) {
          id
          identifier
          name
        }
        categories {
          id
          identifier
          name
          color
        }
        balancesByEntity(startDate: $startDate, endDate: $endDate) {
          entityIdentifier
          isRelevant
          endingValue
        }
      }
    }
  }
`;

export function ChartOfAccountsView() {
  const { activeEntity, activeEntities } = useScope();
  const rootBranch = activeEntity?.rootChartedAccount;
  // Every business in view walked together: the rows under an account are each
  // business's accounts under its own of it, matched by the server, so one only
  // another business has is a row too.
  const entityIdentifiers = useMemo(
    () => (activeEntities || []).map((one) => one.identifier),
    [activeEntities],
  );
  const setActivePanelNodeId = State((store) => store.setActivePanelNodeId);
  const pushToCommandStack = State((store) => store.pushToCommandStack);

  // A cell picked under an entity's column offers the open chat the account,
  // named with that entity. The account's own column is no one entity's, so
  // picking it offers nothing. Its row was asked for with this query, so it is
  // read from the cache.
  const [picked, setPicked] = useState<{
    nodeId: string;
    entityIdentifier: string;
  } | null>(null);
  const account = useQuery<{
    node?: {
      id: string;
      identifier: string;
      name: string;
    } | null;
  }>(CHART_OF_ACCOUNTS_NODE_QUERY, {
    variables: { id: picked?.nodeId, entityIdentifiers },
    skip: !picked,
    fetchPolicy: "cache-first",
  }).data?.node;
  const entity = (activeEntities || []).find(
    (one) => one?.identifier === picked?.entityIdentifier,
  );
  usePageThreadReference(
    account && entity
      ? {
          id: account.id,
          identifier: account.identifier,
          name: `${account.name} · ${entity.name}`,
          typename: "ChartedAccountObject",
        }
      : null,
  );

  const layout = useMemo(
    () => ({
      isSummaryRowVisible: false,
      isCellBordersHidden: false,
      isOpenDefaultDepth: 1,
      fetchingOptions: {
        row: {
          type: "QUERY" as const,
          query: CHART_OF_ACCOUNTS_NODE_QUERY,
          prepQuery: (id) => ({
            id,
            entityIdentifiers,
          }),
          parseQuery: (data) => data?.node,
        },
        column: {
          type: "LOCAL" as const,
          getData: () => {
            const entityAmountColumns = (activeEntities || []).map(
              (entity) => ({
                label: entity?.name || entity?.identifier || "Entity",
                objectKey: `amount_${entity?.identifier || "unknown"}`,
                fieldType: "currency" as const,
                headerFieldType: "reference" as const,
                width: 200,
                renderHeader: () => (
                  <div className="relative w-full h-full flex items-center box-border pl-1 pr-2 min-w-0 gap-2">
                    <div className="min-w-0 overflow-hidden flex-1">
                      <Field
                        type="reference"
                        data={entity}
                        getValue={() => ({
                          __typename: "EntityObject",
                          id: entity?.id,
                          identifier: entity?.identifier,
                          name: entity?.name,
                        })}
                        getValueOptions={() => ({})}
                        events={null}
                        style={{
                          width: "100%",
                          minWidth: 0,
                          overflow: "hidden",
                        }}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      // sx={{
                      //   position: "absolute",
                      //   right: 4,
                      //   top: "50%",
                      //   transform: "translateY(-50%)",
                      // }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const entityIdentifier = entity?.identifier;
                        if (!entityIdentifier) return;
                        pushToCommandStack({
                          commandDefinitionId: "manage-categories",
                          context: {
                            entityIdentifier,
                          },
                        });
                      }}
                    >
                      <Icon ReactIcon={TagsIcon} />
                    </Button>
                  </div>
                ),
                getValue: (branch) => {
                  const entityBalance = (branch?.balancesByEntity || []).find(
                    (item) =>
                      String(item?.entityIdentifier || "") ===
                      String(entity?.identifier || ""),
                  );
                  if (!entityBalance?.isRelevant) {
                    return "-";
                  }
                  const endingValue = entityBalance?.endingValue;
                  return endingValue == null ? "-" : Number(endingValue);
                },
                getCellMeta: (branch) => {
                  const entityBalance = (branch?.balancesByEntity || []).find(
                    (item) =>
                      String(item?.entityIdentifier || "") ===
                      String(entity?.identifier || ""),
                  );
                  return {
                    isIrrelevant: !entityBalance?.isRelevant,
                  };
                },
              }),
            );

            return [
              {
                label: "Account",
                fieldType: "string",
                headerFieldType: "string",
                getValue: (branch) => branch?.name,
                width: 200,
                isLockedLeft: true,
              },
              ...entityAmountColumns,
            ];
          },
        },
      },
      heads: [rootBranch].filter(Boolean),
      getChildrenIds: (data) => {
        return data?.childrenAcrossEntities?.map((child) => child.id) || [];
      },
      // Points the panel at the account, and leaves focus on the cell picked.
      onRowClick: (nodeId: string) => {
        setActivePanelNodeId(nodeId, { autofocus: false });
      },
      onCellFocus: (nodeId: string, objectKey: string) => {
        const columnEntity = (activeEntities || []).find(
          (one) => `amount_${one?.identifier}` === objectKey,
        );
        setPicked(
          columnEntity
            ? { nodeId, entityIdentifier: columnEntity.identifier }
            : null,
        );
      },
    }),
    [
      activeEntities,
      entityIdentifiers,
      pushToCommandStack,
      rootBranch,
      setActivePanelNodeId,
    ],
  );

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 w-fit max-w-full overflow-hidden">
        <Tree layout={layout} />
      </div>
    </div>
  );
}
